<template>
  <eg-table-base
    :items="rubrics"
    :fields="resolvedFields"
    :total="total"
    :selectable="false"
    :sort-by="sortBy"
    :sort-desc="sortDesc"
    :table-loading="tableLoading"
    :initial-loading="initialLoading"
    :is-filtered="isFiltered"
    :click-action="openRubric"
    table-class="rubrics-table"
    data-testid="rubrics-table"
    @getData="$emit('getData', $event)"
  >
    <template #empty-state="{ isFiltered, tableLoading, itemsCount }">
      <empty-state
        v-if="!tableLoading && isFiltered && !itemsCount"
        key="filtered"
        :title="$t('rubrics.noRubricsFound')"
        :description="$t('rubrics.noRubricsFoundDescription')"
        class="m-auto"
        data-testid="empty-state-filtered"
      />
      <empty-state
        v-if="!tableLoading && !isFiltered && !itemsCount"
        key="empty"
        :title="$t('rubrics.noRubricsInLibrary')"
        :description="$t('rubrics.noRubricsInLibraryDescription')"
        class="m-auto"
        data-testid="empty-state"
      />
    </template>

    <template #filters>
      <rubrics-table-filters
        :total="total"
        :available-filters="availableFilters"
        data-testid="eg-table-base-filters"
        :is-filtered="isFiltered"
        @filter-changed="$emit('getData', $event)"
      />
    </template>

    <template #cell(name)="{ item }">
      <cell-name :item="item" data-testid="cell-name" />
    </template>

    <template #cell(gradeLevel)="{ item }">
      <div class="caption-s" data-testid="cell-grade-level">
        <eg-grading-details-level
          v-if="item.gradeLevel"
          :grading-details-level="item.gradeLevel"
          size="medium"
        />
        <span v-else class="text-gray">—</span>
      </div>
    </template>

    <template #cell(intensity)="{ item }">
      <div class="caption-s" data-testid="cell-intensity">
        <span v-if="item.intensity">
          {{ getIntensityDisplayText(item.intensity) }}
        </span>
        <span v-else class="text-gray">—</span>
      </div>
    </template>

    <template #cell(createdBy)="{ item }">
      <div class="caption-s" data-testid="cell-created-by">
        <span v-if="getCreatedByText(item)">
          {{ getCreatedByText(item) }}
        </span>
        <span v-else class="text-gray">—</span>
      </div>
    </template>

    <template #cell(createdAt)="{ value }">
      <span class="caption-s">{{ formatDate(value) }}</span>
    </template>

    <template #cell-actions="{ item }">
      <div class="d-flex align-items-center justify-content-end flex-gap">
        <eg-button
          v-if="showEdit"
          icon="pen-edit"
          icon-color="#1C0F30"
          size="extra-small"
          :to="`/rubric/edit/${item.id}`"
          data-testid="edit-rubric"
        />
        <eg-button
          icon="read-only-view"
          icon-color="#1C0F30"
          size="extra-small"
          data-testid="view-rubric"
          @click.stop="openRubricPreview(item)"
        />
        <!-- Standalone delete button for Shared Rubrics -->
        <eg-button
          v-if="showDelete && !showShare"
          icon="delete"
          icon-color="#DC3545"
          size="extra-small"
          data-testid="delete-rubric"
          @click.stop="confirmDelete(item)"
        />
        <!-- Dropdown with Share and Delete for My Rubrics -->
        <rubric-actions-dropdown
          v-if="showShare && showDelete"
          :rubric="item"
          :show-share="showShare"
          :show-edit="false"
          :show-delete="showDelete"
          data-testid="rubric-actions-dropdown"
          @delete="$emit('delete', $event)"
          @share="$emit('share', $event)"
        />
      </div>
    </template>
  </eg-table-base>
</template>

<script>
import formatDate from "@/utils/formatDate";
import { getIntensityDisplayText, formatAuthorValue } from "@/utils/format";
import { EgTableBase } from "@/components/global/eg/eg_table";
import EmptyState from "@/components/global/empty_state.vue";
import EgGradingDetailsLevel from "@/components/global/eg/eg-tags/eg_grading_details_level.vue";
import RubricsTableFilters from "./filters/rubrics_table_filters.vue";
import CellName from "./table/cell_name.vue";
import RubricActionsDropdown from "./rubric_actions_dropdown.vue";
import { openRubricPreview as showRubricPreview } from "@/components/dashboard/components/open_rubric_preview";

export default {
  name: "RubricsTable",

  components: {
    EgTableBase,
    EmptyState,
    EgGradingDetailsLevel,
    RubricsTableFilters,
    CellName,
    RubricActionsDropdown,
  },

  props: {
    rubrics: {
      type: Array,
      required: true,
    },
    availableFilters: {
      type: Array,
      default: () => ["gradeLevel", "languageId", "createdAt"],
    },
    fields: {
      type: Array,
      required: false,
      default: null,
    },
    total: {
      type: Number,
      required: true,
    },
    selectedAll: {
      type: Boolean,
      default: false,
    },
    sortBy: {
      type: String,
      default: "",
    },
    sortDesc: {
      type: Boolean,
      required: false,
      default: false,
    },
    tableLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    initialLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    isFiltered: {
      type: Boolean,
      default: false,
    },
    showShare: {
      type: Boolean,
      default: true,
    },
    showEdit: {
      type: Boolean,
      default: true,
    },
    showDelete: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    resolvedFields() {
      if (this.fields) return this.fields;
      return [
        {
          key: "name",
          label: this.$t("rubrics.nameField"),
          tdClass: "text-truncate",
          columnClass: "col-name",
          egSortable: true,
        },
        {
          key: "gradeLevel",
          label: this.$t("rubrics.gradeField"),
          columnClass: "col-grade-level",
          egSortable: false,
        },
        {
          key: "intensity",
          label: this.$t("rubrics.intensityField"),
          columnClass: "col-intensity",
          egSortable: true,
        },
        {
          key: "language",
          label: this.$t("rubrics.languageField"),
          columnClass: "col-language",
          egSortable: true,
        },
        {
          key: "createdAt",
          label: this.$t("rubrics.createdDateField"),
          columnClass: "col-created-date",
          egSortable: true,
        },
        {
          key: "actions",
          label: this.$t("rubrics.actionsField"),
          tdClass: "text-right",
          columnClass: "col-actions",
        },
      ];
    },
  },

  methods: {
    formatDate,
    getIntensityDisplayText,

    getCreatedByText(item) {
      if (item.type === "system") {
        return this.$t("rubrics.essayGrader");
      }
      return formatAuthorValue(item.author, this.$store);
    },

    openRubric(item) {
      this.$router.push({
        name: "rubric-edit",
        params: { id: item.id },
      });
    },

    openRubricPreview(item) {
      showRubricPreview(item);
    },

    async confirmDelete(item) {
      const isConfirmed = await this.$showDeleteConfirmationModal({
        title: this.$t("rubrics.deleteRubricConfirm"),
        message: this.$t("rubrics.rubricCannotBeAccessed"),
      });

      if (isConfirmed) {
        this.$emit("delete", [item.id]);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.text-gray {
  color: var(--gray, #6c757d);
}
</style>

<style lang="scss">
.content:has(.rubrics-view-container:first-child) {
  padding: 0 !important;
  .eg-new-button--ordinary {
    &:hover {
      background-color: var(--color-surface-neutral-press);
    }
  }
  .rubric-actions-dropdown {
    .dropdown-toggle {
      max-width: 24px;
      padding: 1px;
      &:hover {
        background-color: var(--color-surface-neutral-press);
      }
    }
  }
}
</style>
